import React, { useMemo, useState } from 'react'
import { makeStyles, createStyles, Theme, Typography } from '@material-ui/core'
import Button from '../Button/Button'
import { Cart, ProductToAdd, Products } from '../../typings/base'
import clsx from 'clsx'
import CMSText, { isPrismicText } from '../Global/CMSText'
import AboveProductDescriptionText, {
  hasAboveProductDescriptionText,
} from '../Products/AboveProductDescriptionText'
import { connect } from 'react-redux'
import { useCart } from '../../hooks/useCart'
import InfoIcon from '@material-ui/icons/InfoOutlined'
import GlobalModal from '../Global/GlobalModal'
import SecondHandBackInStockAlertButton from '../BackInStockAlert/SecondHandBackInStockAlertButton'
import { GetMapDispatchTypes } from '../../interfaces/common/redux'
import { RootState } from '../../redux/reducers/rootReducer'
import BraSizeGuideDrawer from '../BraSizeGuide/BraSizeGuideDrawer'
import { hideSecondHandSizes } from '../../utils/constants'
import {
  getVariantChoiceLabel,
  productVariantsHaveAnyDisplayableSize,
  shouldShowVariantSizeButtons,
} from '../../utils/productVariantDisplay'
import QuantitySelector from '../Shop/QuantitySelector'
import { PrismicGeneralData } from '../../interfaces/common/prismicGeneralConfig'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
    },
    sizes: {
      display: 'flex',
      justifyContent: 'flex-start',
      marginTop: theme.spacing(1),
    },
    sizes__label: {
      paddingRight: theme.spacing(1),
      display: 'flex',
      alignItems: 'center',
    },
    state__label: {
      paddingRight: theme.spacing(1),
      display: 'flex',
      alignItems: 'center',
    },
    sizes__list: {
      padding: theme.spacing(0.25, 0.25, 0.25, 0),
      borderRadius: theme.spacing(1),
      marginBottom: theme.spacing(1),
      flexWrap: 'wrap',
      justifyContent: 'flex-start',
    },
    addButton: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: theme.spacing(3),
      [theme.breakpoints.down('sm')]: {
        justifyContent: 'space-between',
        marginTop: theme.spacing(1),
      },
    },
    button: {
      display: 'inline-flex',
      justifyContent: 'flex-end',
      width: '100%',
    },
    sizeButton: {
      height: theme.spacing(4),
      padding: theme.spacing(1, 2),
      margin: theme.spacing(0.5, 0.5, 0.5, 0),
      minWidth: 45,
      backgroundColor: theme.extraColors.greyLight,
      '&:hover': {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
      },
      '&.sold-out': {
        opacity: 0.5,
        color: theme.palette.error.main,
      },
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.5, 1),
        margin: theme.spacing(0.25),
      },
    },
    selected: {
      height: theme.spacing(4),
      padding: theme.spacing(1, 2),
      margin: theme.spacing(0.5, 0.5, 0.5, 0),
      minWidth: 45,
      color: theme.palette.common.white,
      backgroundColor: theme.palette.common.black,
      '&:hover': {
        color: theme.palette.common.white,
        backgroundColor: theme.palette.common.black,
      },
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.5, 1),
        margin: theme.spacing(0.25),
      },
    },
    info__icon: {
      marginLeft: theme.spacing(0.5),
      width: 16,
      '&:hover': {
        opacity: 0.5,
        cursor: 'pointer',
      },
    },
    statesModal: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
    warrantyText: {
      margin: theme.spacing(1),
    },
    quantitySelectorContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      '& .sizes-add__btn': {
        flexBasis: '50%',
      },
    },
  })
)

type TProps = {
  products: Products
  settings: PrismicGeneralData
  t: any
  getTakeBackStock: any
  takeback: any
  currentItem: any
  cart: Cart
  productSettings: any
  selectedQuality: string
  handleStateSelected: (quality: string) => void
  handleSizeSelected: (sku: string) => void
  currentSizeQualities: any
  showQualities: boolean
  noMoreStockAvailable: boolean
  sku: string
  currentVariantCode: string
  currentQualityInCart: (quality: string, sku: string, cart: any) => number
  setSelectedQuality: any
  availableSkus: string[]
  addedToCart?: () => void
  displayWarrantyText?: boolean
  displayBraGuidePanel?: boolean
  secondHandAvailability?: boolean
  aboveAddToCartNode?: React.ReactNode
} & ReturnType<typeof mapStateToProps> &
  GetMapDispatchTypes<typeof mapDispatchToProps>

const TakeBackSize = ({
  products,
  settings,
  t,
  takeBackProps,
  takeback,
  noMoreStockAvailable,
  currentItem,
  cart,
  selectedQuality,
  handleStateSelected,
  handleSizeSelected,
  currentSizeQualities,
  showQualities,
  sku,
  currentVariantCode,
  productSettings,
  currentQualityInCart,
  setSelectedQuality,
  availableSkus,
  addedToCart,
  displayWarrantyText = true,
  displayBraGuidePanel = false,
  sizesNamePositionMap,
  secondHandAvailability,
  aboveAddToCartNode,
}: TProps) => {
  const { addProductToCart } = useCart()
  const classes = useStyles()
  const showAllSize = false
  const showAllQualities = false
  const isStatesInformationsModal = takeback?.data?.states_informations_modal
  const [openStatesModal, setOpenStatesModal] = useState(false)
  const [quantity, setQuantity] = useState(1)

  const [variants, isAllVariantsInStock, allVariantsNotInStock] =
    useMemo(() => {
      const productVariants = Object.values(products?.variants)
      let _variants = []
      if (showAllSize) {
        // if you want display all sizes
        _variants = productVariants
      } else {
        // if you want display available sizes only
        _variants = productVariants.filter((variant) =>
          takeBackProps?.takeBackStock?.some(
            (stock) =>
              stock.lizeeSKU === variant.sku &&
              stock.productsByQuality.length > 0
          )
        )
      }

      // Apply sorting based on sizesNamePositionMap
      _variants.sort((a, b) => {
        const aPosition = sizesNamePositionMap?.[a.size] ?? a.position
        const bPosition = sizesNamePositionMap?.[b.size] ?? b.position
        return aPosition - bPosition
      })

      const _isAllVariantsInStock = productVariants.length === _variants.length
      const allVariantsInStock = productVariants.filter((variant) =>
        takeBackProps?.takeBackStock?.some(
          (stock) =>
            stock.lizeeSKU === variant.sku && stock.productsByQuality.length > 0
        )
      )
      const _allVariantsNotInStock = productVariants.filter(
        (variant) =>
          !allVariantsInStock
            .map((allVariantInStock) => allVariantInStock.sku)
            .includes(variant.sku)
      )

      return [_variants, _isAllVariantsInStock, _allVariantsNotInStock]
    }, [
      products?.variants,
      showAllSize,
      takeBackProps?.takeBackStock,
      sizesNamePositionMap,
    ])

  const handleCloseModal = () => {
    setOpenStatesModal(false)
  }

  // Calculate max available quantity for selected quality
  const maxAvailableQuantity = useMemo(() => {
    if (!selectedQuality) return 0
    const qualityData = currentSizeQualities.find(
      (quality) => quality.quality === selectedQuality
    )
    const totalAvailable = qualityData?.products?.length || 0
    const alreadyInCart = currentQualityInCart(selectedQuality, sku, cart)
    return Math.max(0, totalAvailable - alreadyInCart)
  }, [selectedQuality, currentSizeQualities, sku, cart])

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity < 1) return

    // Limit quantity to available stock
    const limitedQuantity = Math.min(newQuantity, maxAvailableQuantity)
    if (limitedQuantity >= 1) {
      setQuantity(limitedQuantity)
    }
  }

  const handleAddTakeBackProductToCart = () => {
    if (quantity <= 0) {
      return
    }
    let quantityIteration = quantity
    const data: ProductToAdd = {
      productCode: currentItem.code,
      secondHand: 'true',
      units: [],
    }
    const puidsInCartForSkuQuality = (): Set<string> => {
      const ids = new Set<string>()
      cart?.item?.items?.forEach((line) => {
        if (line.product?.variants?.[0]?.sku !== sku) return
        line.units?.forEach((u) => {
          if (u.productCondition === selectedQuality) ids.add(u.puid)
        })
      })
      return ids
    }
    while (quantityIteration > 0) {
      const qualityData = currentSizeQualities.find(
        (currentQuality) => currentQuality.quality === selectedQuality
      )
      if (!qualityData?.products?.length) break

      const reserved = puidsInCartForSkuQuality()
      data.units.forEach((u) => reserved.add(u.puid))

      const nextProduct = qualityData.products.find(
        (p) => !reserved.has(p.puid)
      )
      if (!nextProduct) break
      const { puid } = nextProduct
      data.units.push({
        puid,
        condition: selectedQuality,
      })
      quantityIteration--
    }

    if (!currentItem?.isSimpleProduct) {
      data.variantCode = currentVariantCode.toString()
    }

    addProductToCart(data)
    setQuantity(1)
    // FIX: we commented this line to prevent resetting quality selection after adding to cart
    // setSelectedQuality('')
    if (addedToCart) addedToCart()
  }

  const stockAlertSecondHand =
    takeback?.data?.take_back_stock_alert_enable && !isAllVariantsInStock

  const showSizeAxisLabel =
    !hideSecondHandSizes && productVariantsHaveAnyDisplayableSize(variants)
  const showSizeButtons =
    !hideSecondHandSizes && shouldShowVariantSizeButtons(variants)

  return (
    <div className={classes.root}>
      {!noMoreStockAvailable ? (
        <>
          {showSizeAxisLabel && (
            <div className={`sizes ${classes.sizes}`}>
              <Typography
                component={'div'}
                className={`size__label ${classes.sizes__label}`}
              >
                <strong>
                  <CMSText
                    asText
                    data={settings?.sizes}
                    defaultText={t('texts:general:sizes')}
                  />
                </strong>
                {displayBraGuidePanel && <BraSizeGuideDrawer />}
              </Typography>
            </div>
          )}
          <div className={`sizes__list ${classes.sizes__list}`}>
            {showSizeButtons &&
              variants &&
              variants?.map((variant, index) => {
                return (
                  <Button
                    key={index}
                    size="small"
                    className={
                      variant.sku === sku
                        ? `sizes__btn selected ${classes.selected}`
                        : `sizes__btn ${classes.sizeButton}`
                    }
                    onClick={() => handleSizeSelected(variant.sku)}
                    data-testid={`btn-size-${index}`}
                    disabled={
                      (showAllSize &&
                        takeBackProps?.takeBackStock?.find(
                          (varSku) => varSku.lizeeSKU === variant.sku
                        )?.productsByQuality.length === 0) ||
                      !availableSkus.includes(variant.sku)
                    }
                  >
                    {getVariantChoiceLabel(variant)}
                  </Button>
                )
              })}
            {/**stock alert second hand */}
            {stockAlertSecondHand && (
              <SecondHandBackInStockAlertButton
                prismicTakeback={takeback?.data}
                allVariantsNotInStock={allVariantsNotInStock}
              />
            )}
            {showQualities && (
              <div>
                <Typography
                  component={'div'}
                  className={`state__label ${classes.state__label}`}
                >
                  <strong>
                    <CMSText
                      asText
                      data={takeback?.data?.state_label}
                      defaultText={t('texts:takeBack:select_label_state')}
                    />
                  </strong>
                  {isStatesInformationsModal && (
                    <InfoIcon
                      fontSize="small"
                      onClick={() => setOpenStatesModal(true)}
                      className={classes.info__icon}
                    />
                  )}
                </Typography>
                <GlobalModal
                  open={openStatesModal}
                  onClose={handleCloseModal}
                  modalTitle={{
                    data: takeback?.data?.states_information_modal_title,
                    defaultText: t(
                      'texts:takeBack:take_back_states_information_modal_title'
                    ),
                  }}
                  customClass={'take_back_states_informations'}
                >
                  <div className={classes.statesModal}>
                    <div>
                      <Typography variant={'h4'}>
                        <CMSText
                          asText
                          data={takeback?.data?.perfectcondition}
                          defaultText={takeback?.data?.state_1}
                        />
                      </Typography>
                      <Typography>
                        <CMSText
                          asText
                          data={takeback?.data?.perfectconditiondefinition}
                          defaultText={takeback?.data?.state_1}
                        />
                      </Typography>
                    </div>
                    <div>
                      <Typography variant={'h4'}>
                        <CMSText
                          asText
                          data={takeback?.data?.verygoodcondition}
                          defaultText={takeback?.data?.state_1}
                        />
                      </Typography>
                      <Typography>
                        <CMSText
                          asText
                          data={takeback?.data?.verygoodconditiondefinition}
                          defaultText={takeback?.data?.state_1}
                        />
                      </Typography>
                    </div>
                    {!takeback?.data?.disable_good_condition && (
                      <div>
                        <Typography variant={'h4'}>
                          <CMSText
                            asText
                            data={takeback?.data?.goodcondition}
                            defaultText={takeback?.data?.state_1}
                          />
                        </Typography>
                        <Typography>
                          <CMSText
                            asText
                            data={takeback?.data?.goodconditiondefinition}
                            defaultText={takeback?.data?.state_1}
                          />
                        </Typography>
                      </div>
                    )}
                  </div>
                </GlobalModal>
                {currentSizeQualities.map((quality, index) => {
                  const Btn = () => (
                    <Button
                      size="small"
                      className={
                        quality.quality === selectedQuality
                          ? `sizes__btn selected ${classes.selected}`
                          : `sizes__btn ${classes.sizeButton}`
                      }
                      disabled={quality.disabled}
                      onClick={() => handleStateSelected(quality)}
                    >
                      {quality.title}
                    </Button>
                  )

                  if (showAllQualities) {
                    return <Btn key={index} />
                  }
                  if (!quality.disabled) {
                    return <Btn key={index} />
                  }
                  return null
                })}
              </div>
            )}
          </div>
          {aboveAddToCartNode}
          {settings?.second_hand_has_quantity_selector &&
          secondHandAvailability ? (
            <div
              className={clsx(
                classes.quantitySelectorContainer,
                'second-hand-quantity-selector-container'
              )}
            >
              <QuantitySelector
                value={quantity}
                handleChange={handleQuantityChange}
                min={1}
                max={maxAvailableQuantity}
                variantSku={sku}
                disable={
                  selectedQuality === '' ||
                  currentSizeQualities.find(
                    (quality) => quality.quality === selectedQuality
                  )?.disabled ||
                  maxAvailableQuantity === 0
                }
              />
              <Button
                variant="contained"
                color="secondary"
                data-testid={'btn-addToCart'}
                id="btn-addToCart"
                disabled={
                  selectedQuality === '' ||
                  currentSizeQualities.find(
                    (quality) => quality.quality === selectedQuality
                  )?.disabled
                }
                className="sizes-add__btn"
                onClick={handleAddTakeBackProductToCart}
              >
                <CMSText
                  asText
                  data={productSettings?.data?.buy_button}
                  defaultText={t('texts:single_product:buy_button')}
                />
              </Button>
            </div>
          ) : (
            <Button
              variant="contained"
              color="secondary"
              data-testid={'btn-addToCart'}
              id="btn-addToCart"
              disabled={
                selectedQuality === '' ||
                currentSizeQualities.find(
                  (quality) => quality.quality === selectedQuality
                )?.disabled
              }
              className="sizes-add__btn"
              onClick={handleAddTakeBackProductToCart}
            >
              <CMSText
                asText
                data={productSettings?.data?.buy_button}
                defaultText={t('texts:single_product:buy_button')}
              />
            </Button>
          )}
          <AboveProductDescriptionText
            productSettings={productSettings}
            className={classes.warrantyText}
          />
        </>
      ) : (
        <>
          <Typography variant="h4" className={'product__unavailable'}>
            <CMSText
              asText
              data={productSettings?.data?.sold_out}
              defaultText={t('texts:packs:victim_of_success')}
            />
          </Typography>
          {/**stock alert second hand */}
          {stockAlertSecondHand && (
            <SecondHandBackInStockAlertButton
              prismicTakeback={takeback?.data}
              allVariantsNotInStock={allVariantsNotInStock}
            />
          )}
        </>
      )}
      {isPrismicText(takeback?.data?.warranty_text) &&
        displayWarrantyText &&
        !hasAboveProductDescriptionText(productSettings) && (
          <Typography
            component={'div'}
            variant={'caption'}
            className={clsx(classes.warrantyText, 'warranty_text')}
          >
            <CMSText data={takeback?.data?.warranty_text} />
          </Typography>
        )}
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  takeBackProps: state.takeBack,
  sizesNamePositionMap: state.taxons.sizesNamePositionMap,
})
const mapDispatchToProps = {}

export default connect(mapStateToProps, mapDispatchToProps)(TakeBackSize)
